// src/pages/Jobspage/JobKanban.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import "./JobKanban.css";

// Kanban columns configuration - Full workflow
// Flow: Pending → Approved → Assigned → Picked Up → In Transit → Delivered
const KANBAN_COLUMNS = [
  { id: 'Pending', label: 'Pending', color: '#f59e0b' },
  { id: 'Approved', label: 'Approved', color: '#0891b2' },
  { id: 'Assigned', label: 'Assigned', color: '#3b82f6' },
  { id: 'Picked Up', label: 'Picked Up', color: '#7c3aed' },
  { id: 'In Transit', label: 'In Transit', color: '#8b5cf6' },
  { id: 'Delivered', label: 'Delivered', color: '#10b981' },
  { id: 'Exception', label: 'Exception', color: '#ef4444' },
  { id: 'Returned', label: 'Returned', color: '#6b7280' },
  { id: 'Cancelled', label: 'Cancelled', color: '#9ca3af' },
];

// Check if job can have driver assigned (only Approved status)
const canAssignDriver = (job) => {
  return job.status === 'Approved' && !job.driver;
};

// Priority badge helper
const getPriorityClass = (priority) => {
  const priorityMap = {
    'Priority order': 'kanban-priority-urgent',
    'High': 'kanban-priority-high',
    'Medium': 'kanban-priority-normal',
    'Low': 'kanban-priority-low',
  };
  return priorityMap[priority] || 'kanban-priority-normal';
};

export default function JobKanban({ jobs, onAssignDriver, onUpdateStatus, loading }) {
  const navigate = useNavigate();

  // Group jobs by status
  const groupedJobs = KANBAN_COLUMNS.reduce((acc, column) => {
    acc[column.id] = jobs.filter(job => job.status === column.id);
    return acc;
  }, {});

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle card click
  const handleCardClick = (job) => {
    navigate(`/jobs/${job._id}`);
  };

  // Handle drag start
  const handleDragStart = (e, job) => {
    e.dataTransfer.setData('jobId', job._id);
    e.dataTransfer.setData('currentStatus', job.status);
    e.target.classList.add('dragging');
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  // Status transition rules (must match backend)
  const allowedTransitions = {
    'Pending': ['Approved', 'Cancelled'],
    'Approved': ['Assigned', 'Cancelled'],
    'Assigned': ['Picked Up', 'Cancelled'],
    'Picked Up': ['In Transit', 'Exception'],
    'In Transit': ['Delivered', 'Exception'],
    'Delivered': ['Returned'],
    'Exception': ['Returned', 'In Transit'],
    'Returned': [],
    'Cancelled': [],
  };

  // Handle drop
  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');

    const jobId = e.dataTransfer.getData('jobId');
    const currentStatus = e.dataTransfer.getData('currentStatus');

    if (currentStatus !== newStatus) {
      // Find the job being dropped
      const job = jobs.find(j => j._id === jobId);

      // Check if transition is allowed
      if (!allowedTransitions[currentStatus]?.includes(newStatus)) {
        alert(`Cannot change status from "${currentStatus}" to "${newStatus}"`);
        return;
      }

      // If dropping to "Assigned" and job has no driver, open assign modal
      if (newStatus === 'Assigned' && job && !job.driver) {
        if (onAssignDriver) {
          onAssignDriver(job);
        }
        return;
      }

      // Statuses that require a driver
      const driverRequiredStatuses = ['Assigned', 'Picked Up', 'In Transit', 'Delivered'];
      if (driverRequiredStatuses.includes(newStatus) && job && !job.driver) {
        alert(`Cannot set status to "${newStatus}" without a driver assigned.`);
        return;
      }

      // Otherwise, update status normally
      if (onUpdateStatus) {
        onUpdateStatus(jobId, newStatus);
      }
    }
  };

  if (loading) {
    return (
      <div className="kanban-loading">
        <div className="kanban-spinner"></div>
        <p>Loading jobs...</p>
      </div>
    );
  }

  return (
    <div className="kanban-board">
      {KANBAN_COLUMNS.map((column) => (
        <div
          key={column.id}
          className="kanban-column"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, column.id)}
        >
          <div className="kanban-column-header" style={{ borderTopColor: column.color }}>
            <div className="kanban-column-title">
              <span className="kanban-column-dot" style={{ backgroundColor: column.color }}></span>
              <h3>{column.label}</h3>
              <span className="kanban-column-count">{groupedJobs[column.id]?.length || 0}</span>
            </div>
          </div>

          <div className="kanban-column-body">
            {groupedJobs[column.id]?.length === 0 ? (
              <div className="kanban-empty">
                <p>No jobs</p>
              </div>
            ) : (
              groupedJobs[column.id].map((job) => (
                <div
                  key={job._id}
                  className="kanban-card"
                  draggable
                  onDragStart={(e) => handleDragStart(e, job)}
                  onDragEnd={handleDragEnd}
                  onClick={() => handleCardClick(job)}
                >
                  <div className="kanban-card-header">
                    <span className="kanban-job-id">{job.jobId}</span>
                    <span className={`kanban-priority ${getPriorityClass(job.priority)}`}>
                      {job.priority || 'Medium'}
                    </span>
                  </div>

                  <div className="kanban-card-route">
                    <div className="kanban-route-point">
                      <span className="kanban-route-icon pickup"></span>
                      <span className="kanban-route-text">{job.from?.name || job.from?.address?.substring(0, 20) || 'N/A'}</span>
                    </div>
                    <div className="kanban-route-line"></div>
                    <div className="kanban-route-point">
                      <span className="kanban-route-icon delivery"></span>
                      <span className="kanban-route-text">{job.to?.name || job.to?.address?.substring(0, 20) || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="kanban-card-footer">
                    <div className="kanban-card-info">
                      {job.driver ? (
                        <div className="kanban-driver">
                          <span className="kanban-driver-avatar">
                            {job.driver.firstName?.[0] || 'D'}
                          </span>
                          <span className="kanban-driver-name">
                            {job.driver.firstName} {job.driver.lastName?.[0]}.
                          </span>
                        </div>
                      ) : canAssignDriver(job) ? (
                        <button
                          className="kanban-assign-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAssignDriver && onAssignDriver(job);
                          }}
                        >
                          + Assign
                        </button>
                      ) : (
                        <span className="kanban-no-driver">—</span>
                      )}
                    </div>
                    <span className="kanban-date">{formatDate(job.scheduledDate || job.createdAt)}</span>
                  </div>

                  {job.client && (
                    <div className="kanban-card-client">
                      {job.company?.name || `${job.client.firstName} ${job.client.lastName || ''}`}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
